<script setup>
import Skeleton from '/app/components/Skeleton.vue';
import LocationPicker from '/app/components/form/LocationSearch.vue';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import { Icon } from '@bignerve/ui-core';
import { useFuse } from '@vueuse/integrations/useFuse';
import { ref, computed, watch, inject } from 'vue';
import { useFormContext } from '/app/composables/form';
import { useCategories } from '/app/composables/categories';
import { useBus } from '/app/composables/bus';
import { useI18n } from 'vue-i18n';

const stepper = inject('stepper');
const builder = inject('builder');

const form = useFormContext();
const { emit: busEmit } = useBus();
const { t } = useI18n();
const { categories: rootCategories } = useCategories();

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    categoryTree: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['update:categoryTree', 'update:modelValue', 'change', 'selected']);

const TOOLS = {
    'qq': {
        icon: 'https://category-icons.bignerve.com/qq-question-question-category-icon.svg',
        text: 'QQ: Find a better Question',
    },
    'test-an-idea': {
        icon: 'https://category-icons.bignerve.com/test-idea-category-icon.svg',
        text: 'Test my Idea',
    },
    'improve-an-idea': {
        icon: 'https://category-icons.bignerve.com/improve-category-icon.svg',
        text: 'Make it better: Improve my idea',
    },
};

const tree = ref([]);
const forced = ref(null);
const searchTerm = ref('');

const current = computed(() =>
    tree.value.length > 0 ? tree.value[tree.value.length - 1] : null,
);

const categoryMain = computed(() => {
    const parent = (c) => c && c.parent ? parent(c.parent) : c;
    return parent(current.value);
});

const loopable = computed(() =>
    Array.from(tree.value).reverse().find((item) => item.children.length > 0)
        || (current.value && current.value.parent)
        || null,
);

const hasTool = computed(() => TOOLS[form.fields.solution_format]);

const hasSelection = computed(() =>
    (builder.usingTool() && hasTool.value) || loopable.value,
);

const selectable = computed(() =>
    (current.value && !['caption'].includes(current.value.slug))
        || (builder.usingTool() && hasTool.value),
);

const categories = computed(() => {
    const cats = loopable.value
        ? loopable.value.children
        : rootCategories.value;

    return orderBy(cats, ['name'], ['asc']);
});

const note = computed(() => {
    if (loopable.value && !isEmpty(loopable.value.description)) {
        return loopable.value.description;
    }

    return t('component.pick-cateogry.note');
});

// Recursively flatten all categories and their children
const allCategories = computed(() => {
    const flattened = [];

    const flatten = (cats) => {
        for (const cat of cats) {
            flattened.push(cat);
            if (cat.children && cat.children.length > 0) {
                flatten(cat.children);
            }
        }
    };

    if (loopable.value) {
        flatten(loopable.value.children);
    } else {
        flatten(rootCategories.value);
    }

    return flattened;
});

// useFuse at setup scope (not inside a computed). It returns reactive `results`
// that auto-update when searchTerm or allCategories changes.
const { results: fuseResults } = useFuse(searchTerm, allCategories, {
    fuseOptions: {
        keys: ['name', 'slug'],
        threshold: 0.3,
        includeScore: true,
    },
    resultLimit: 50,
    matchAllWhenSearchEmpty: false,
});

const searchResults = computed(() => {
    if (!searchTerm.value.trim()) {
        return categories.value;
    }

    return fuseResults.value ? fuseResults.value.map((result) => result.item) : [];
});

const filteredCategories = computed(() => {
    if (!searchTerm.value.trim()) {
        return categories.value;
    }

    // Remove duplicates based on category ID
    const uniqueResults = [];
    const seenIds = new Set();

    for (const category of searchResults.value) {
        if (!seenIds.has(category.id)) {
            seenIds.add(category.id);
            uniqueResults.push(category);
        }
    }

    return uniqueResults;
});

function find(category, id) {
    if (category.id === id) {
        return category;
    }

    if (category.children.length > 0) {
        for (let i = 0; i < category.children.length; i++) {
            const child = category.children[i];
            const found = find(child, id);

            if (found) {
                return found;
            }
        }
    }

    return null;
}

function select({ category, force = false, auto = false }) {
    forced.value = force ? category : null;

    (current.value && current.value.children.length === 0) || force
        ? tree.value = [...tree.value.slice(0, -1), category]
        : tree.value = [...tree.value, category];

    if (!auto) {
        emit('selected', category);
    }
}

function back() {
    if (tree.value.length === 0 && builder.usingTool()) {
        form.fields.solution_format = null;
    } else {
        tree.value.splice(
            tree.value.findIndex((item) => item.id === loopable.value.id),
        );
    }
}

function selectById(id, { auto, force } = {}) {
    if (!id) return;

    const category = find(
        { children: rootCategories.value },
        id,
    );

    if (category) {
        select({ category, auto, force });
    }
}

function fallback(event) {
    event.target.src = 'https://category-icons.bignerve.com/article-draft-icon.svg';
}

function reset() {
    tree.value = [];
}

function clearSearch() {
    searchTerm.value = '';
}

// Watchers
watch(tree, (categories, history) => {
    if (isEqual(categories, history) && !forced.value) {
        return;
    }

    const category = categories.length > 0
        ? categories[categories.length - 1]
        : { id: null, children: [] };

    emit('update:categoryTree', categories);

    if (!category.id) {
        return;
    }

    if (!category.children.length || (forced.value && forced.value.id === category.id)) {
        emit('update:modelValue', category.id);
        emit('change', category);
    }
});

watch(rootCategories, () => {
    selectById(props.modelValue, { auto: true, force: true });
});

watch(categoryMain, (category) => {
    busEmit('challenge:creation-flow:category-main', category);
});

watch(current, (category) => {
    if (!props.categoryTree.length && category) {
        select({ category, auto: true });
    }
});

watch(
    () => props.modelValue,
    (newValue, oldValue) => {
        if (!newValue) {
            reset();
            return;
        }

        if (newValue === oldValue) {
            return;
        }

        selectById(newValue, { auto: true, force: true });
    },
    { immediate: true },
);
</script>

<template>
    <div>
        <div class="challenge-categories">
            <div class="grid grid-cols-12 gap-4 mt-3">
                <div v-show="hasSelection" class="col-span-12 sm:col-span-3 xl:col-span-2 challenge-categories__back-btn">
                    <button aria-label="Back" type="button" class="stepper-back-button" @click.prevent="back">
                        <span aria-hidden="true" class="stepper-back-button__icon icon-arrow-alt-circle-left-regular"></span>
                        Back
                    </button>
                </div>

                <div class="challenge-categories__title" :class="{ 'col-span-12 sm:col-span-9 xl:col-span-10': hasSelection, 'col-span-12': ! hasSelection }">
                    <div class="flex items-center justify-between gap-4 flex-wrap">
                        <h2 class="stepper-title !m-0 whitespace-nowrap">Choose a category</h2>

                        <!-- Reset and Search Controls -->
                        <div class="flex sm:items-center gap-3 flex-col sm:flex-row w-full sm:w-auto" v-if="!builder.usingTool() || !hasTool">
                            <!-- Reset Button -->
                            <button
                                v-if="tree.length > 0"
                                aria-label="Reset to all categories"
                                type="button"
                                class="hover:bg-secondary-200 hover:text-secondary-800 group flex items-center rounded-md bg-secondary-100 text-secondary-600 text-sm font-medium px-4 py-2 focus:outline-none"
                                @click="reset">
                                <span aria-hidden="true" class="icon-th-solid mr-2"></span>
                                All Categories
                            </button>

                            <!-- Search Input -->
                            <div class="relative border border-dust-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 w-full sm:w-auto">
                                <input
                                    v-model="searchTerm"
                                    type="text"
                                    placeholder="Search categories..."
                                    class="w-full sm:w-64 px-3 py-2 pr-10 text-sm rounded-md focus:border-transparent"
                                />
                                <button v-if="searchTerm"
                                    type="button"
                                    class="absolute right-0 top-1/2 -translate-y-1/2 bottom-0 px-2 text-dust-400 rounded-md hover:text-dust-600"
                                    @click="clearSearch">
                                    <Icon as="times-circle" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="hasSelection" class="col-span-6 sm:col-span-3 xl:col-span-2 challenge-categories__selected-category">
                    <button aria-label="Cagetory"
                        v-if="loopable"
                        :class="{ '!cursor-not-allowed': !selectable }"
                        type="button"
                        class="category-tile is-active is-selected !mb-2"
                        @click.prevent="selectable && select({ category: loopable, force: true })"
                    >
                        <img class="category-tile__icon" :src="`https://category-icons.bignerve.com/${loopable.icon}`" @error="fallback" alt="Category">
                        <span class="category-tile__name">{{ loopable.name }}</span>
                    </button>
                    <button aria-label="Cagetory"
                        v-else-if="builder.usingTool() && hasTool"
                        type="button"
                        class="category-tile is-active is-selected transition duration-100"
                    >
                        <img
                            :src="TOOLS[form.fields.solution_format].icon"
                            alt=""
                            class="category-tile__icon"
                        >
                        <span class="category-tile__name">{{ TOOLS[form.fields.solution_format].text }}</span>
                    </button>

                    <span v-if="selectable" class="text-secondary italic inline-block w-full text-center">
                        Choose this
                    </span>
                </div>

                <div :class="{ 'col-span-12 sm:col-span-9 xl:col-span-10': hasSelection, 'col-span-12': ! hasSelection }">
                    <div v-if="! loopable || loopable.slug !== 'local-civics'" class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <template v-if="!rootCategories.length">
                            <div v-for="i of 9" :key="`placeholder-category-${i}`" class="overflow-hidden">
                                <div class="w-full animate-pulse rounded-lg" style="background: #9b9b9b; height: 135px"></div>
                            </div>
                        </template>

                        <!-- No results message -->
                        <div v-if="searchTerm && filteredCategories.length === 0" class="col-span-full flex flex-col items-center justify-center py-16 text-center">
                            <span class="icon-search text-6xl text-dust-400 mb-4"></span>
                            <p class="text-dust-600 font-medium mb-2">
                                <template v-if="loopable">
                                    No results found in "{{ loopable.name }}" matching "{{ searchTerm }}"
                                </template>
                                <template v-else>
                                    No categories found matching "{{ searchTerm }}"
                                </template>
                            </p>
                            <p class="text-dust-500 text-sm">
                                Try adjusting your search terms
                            </p>
                        </div>

                        <div v-for="category in filteredCategories" :key="`${category.id}${category.parent?.id}`" :id="`${category.id}${category.parent?.id}`">
                            <button aria-label="Cagetory"
                                type="button"
                                class="category-tile is-in-grid transition duration-100 bg-dust-500"
                                :class="{ 'is-active': current && current.id === category.id }"
                                @click.prevent="select({ category, force: current && current.id === category.id })"
                            >
                                <img class="category-tile__icon" :src="`https://category-icons.bignerve.com/${category.icon}`" @error="fallback" alt="">
                                <span class="category-tile__name">{{ category.name }}</span>
                            </button>
                        </div>
                    </div>
                    <div v-else>
                        <div class="category-choose-place-wrapper">
                            <h3 class="mt-0 mb-3">Choose what country, region, state, or city this involves:</h3>

                            <LocationPicker
                                :modelValue="form.fields.location"
                                name="region"
                                prepend-icon="map-marker text-dust-700"
                                id="region"
                                placeholder="Country, Region, State, or City"
                                class="w-full"
                                @update:modelValue="({ formatted }) => (form.fields.location = formatted, select({ category: current.children[0] }))"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-span-12 sm:col-span-8 xl:col-span-8 mt-5">
                    <div class="info-box">
                        <span aria-hidden="true" class="info-box__icon icon-exclamation-circle-solid"></span>
                        <p  class="info-box__content" v-html="note" ></p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
